import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Download, Check, X, Minus } from 'lucide-react';
import { comparisonPages } from '../data/knowledgeHub';
import { Helmet } from 'react-helmet-async';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const CompareArticle = () => {
  const { slug } = useParams();
  const page = comparisonPages.find(p => p.slug === slug);

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const renderCell = (value) => {
    if (value === true) return <Check className="w-6 h-6 text-green-600 mx-auto" />;
    if (value === false) return <X className="w-6 h-6 text-red-400 mx-auto" />;
    if (value === "partial" || value === "basic") return <Minus className="w-6 h-6 text-yellow-500 mx-auto" />;
    return <span className="text-sm font-medium">{value}</span>;
  };

  if (!page) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 container text-center">
          <h1 className="text-3xl font-bold mb-4">Comparison Not Found</h1>
          <Link to="/compare" className="text-orange-600 hover:underline">View All Comparisons</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{page.title} | TypeIt4U</title>
        <meta name="description" content={page.description} />
        <meta name="keywords" content={page.keywords?.join(', ')} />
        <link rel="canonical" href={`https://typeit4u.com/compare/${page.slug}`} />
      </Helmet>

      <Navbar />
      
      {/* Breadcrumb */}
      <section className="pt-28 pb-4 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <Link to="/compare" className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            All Comparisons
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="pb-12 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="text-center">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-2"
                  style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                >
                  <span className="text-white font-bold text-xl">T4U</span>
                </div>
                <span className="font-medium">TypeIt4U</span>
              </div>
              <span className="text-3xl font-bold text-gray-400">VS</span>
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <span className="text-gray-600 font-bold text-xl">{page.competitor.charAt(0)}</span>
                </div>
                <span className="font-medium">{page.competitor}</span>
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              {page.title}
            </h1>
            <p className="text-lg text-gray-600">{page.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-white">
        <div className="container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-orange-100 to-pink-100">
                  <th className="text-left p-5 font-semibold">Feature</th>
                  <th className="text-center p-5 font-semibold" style={{ color: '#FF6B9D' }}>
                    TypeIt4U
                  </th>
                  <th className="text-center p-5 font-semibold text-gray-600">
                    {page.competitor}
                  </th>
                </tr>
              </thead>
              <tbody>
                {page.comparison.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-5 text-gray-800 font-medium">{row.feature}</td>
                    <td className="p-5 text-center">
                      {renderCell(row.typeit4u)}
                    </td>
                    <td className="p-5 text-center text-gray-500">
                      {renderCell(row.competitor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Verdict */}
          {page.verdict && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="mt-8 bg-gradient-to-r from-orange-100 to-pink-100 rounded-xl p-6 border-l-4 border-orange-500"
            >
              <h3 className="font-semibold text-lg mb-2" style={{ color: '#0A0A0A' }}>Our Verdict</h3>
              <p className="text-gray-700">{page.verdict}</p>
            </motion.div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 text-xs text-gray-500 bg-gray-50 rounded-xl p-4">
            <strong>Disclaimer:</strong> {page.competitor} is a trademark of its respective owner. TypeIt4U is an independent product and is not affiliated with or endorsed by {page.competitor} or its parent company. This comparison is based on publicly available information and features as of 2025.
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-12 text-center bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-10 border border-orange-100"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Ready to Switch to TypeIt4U?
            </h3>
            <p className="text-gray-600 mb-6">
              Join thousands of Indian users who chose TypeIt4U for better typing.
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              <Download className="w-5 h-5" />
              Download TypeIt4U Free
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompareArticle;
